import React, { useEffect } from "react"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"
import GetSpace from "../../../utils/space"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import { faClose, faFile, faFileSignature, faLayerGroup } from "@fortawesome/free-solid-svg-icons"
import InputField from "../../../components/InputField/InputField"
import { ViewRegister } from "../../../components/ViewRegister/ViewRegister"
import { useRegister } from "../../../hooks/Register/useRegister"
import { useError } from "../../../context/useErrorContext"
import { EEnableStatusType } from "../../../Enum/EEnableStatusType"
import { SIZE } from "../../../configuration/Sizes"
import THEME from "../../../configuration/Theme"
import { useConfirmation } from "../../../context/useConfirmationContext"
import { INPUT_SIZE } from "../../../configuration/InputSize"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { CategoryEntity } from "../../../Objects/Category/CategoryEntity"
import { useGetCategory } from "../../../hooks/Category/useGetCategory"
import { useSaveCategory } from "../../../hooks/Category/useSaveCategory"
import { useCreateCategory } from "../../../hooks/Category/useCreateCategory"
import { useRemoveCategory } from "../../../hooks/Category/useRemoveCategory"
import { CanView } from "../../../components/CanView/CanView"
import { Permissions } from "../../../Enum/PermissionsType"
import { useTranslation } from "react-i18next"
import { usePermission } from "../../../hooks/Permissions/usePermission"
import { IconSelector } from "../../../components/IconSelector/IconSelector"
import { CopyDraftAlert } from "../../../components/CopyDraftAlert/CopyDraftAlert"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import { AssociationsPopup } from "../../../components/Associations/AssociationsPopup"
import { comboMemory, catalogItemsMemory, productsMemory } from "../../../Objects/Memory/Memory"
import { resolveCategoryAssociations } from "../../../components/Associations/associationResolvers"

interface CategoryPupopProbs {
  title: string
  category?: CategoryEntity
  onClonse: () => void
  onCopy?: () => void
}

export const CategoryPupop: React.FC<CategoryPupopProbs> = ({ category, title, onClonse, onCopy }) => {
  const { t } = useTranslation("category")

  const isCopiedDraft = category?.id === -1

  const [showAssociations, setShowAssociations] = React.useState(false)

  const canEdite = !usePermission(Permissions.CanEditeCategories)

  const { handleEnableRegister, handleDisableRegister, isRegisterEnable } = useRegister()

  const { handleGetCategory } = useGetCategory()

  const { setNewConfirmation } = useConfirmation()

  const { handleRemove } = useRemoveCategory()

  const { handleSavesCategory } = useSaveCategory()

  const { setError } = useError()

  const {
    name,
    description,
    status,
    icon,
    handleNameChange,
    handleDescriptionChange,
    handleStatusChange,
    handleIconChange,
    handleSubmitForm,
    handleInitialValue,
  } = useCreateCategory()

  useEffect(() => {
    handleInitialValue(category)
  }, [category])

  const handleTrySubmit = (event: React.FormEvent) => {
    event.preventDefault()
    const result = handleSubmitForm()

    if ("errors" in result) {
      const errorMessages = result.errors.map((e) => e.message).join("\n")
      setError(t("general:label_invalid"), errorMessages)

      return
    }

    handleSavesCategory(result, () => {
      onClonse()
      handleGetCategory(() => {})
    })
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert}>
            <TitlePageResponsiveIconSmall
              title={title}
              icon={faLayerGroup}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className={defaultStyle.ButtonsActions}>
                  <>
                    {category && category.id !== -1 && (
                      <div style={{ width: "100px", marginRight: "5px" }}>
                        <GenericDropDown
                          data={Object.mapEnumToStrings(EEnableStatusType, {
                            exclude: [EEnableStatusType.All],
                          })}
                          initialState={status}
                          handlerFuncion={handleStatusChange}
                          disable={canEdite}

                  descriptionKey="interactionDescriptions:pages.products.category.categoryPupop.select.select" />

                        <GetSpace right={10} />
                      </div>
                    )}

                    <PopupHelpButton
                      helpKey="pages.products.category.categorypupop.1"
                      variant="global"
                      title={t("helpPopup:global.category.title")}
                      description={t("helpPopup:global.category.description")}
                      bullets={t("helpPopup:global.category.bullets", { returnObjects: true }) as string[]}
                    />

                    <GetSpace right={5} />

                    <ButtonIcon
                      sizeNumber={SIZE.buttonCloseIconSize}
                      bgColor={THEME.colors.buttonBGCloseColor}
                      iconColor={THEME.colors.buttonFGCloseColor}
                      icon={faClose}
                      onClick={onClonse}
                      size="small"

                  descriptionKey="interactionDescriptions:pages.products.category.categoryPupop.button.onClonse" />
                  </>
                </div>
              }
            />

            {isCopiedDraft && <CopyDraftAlert />}

            <form onSubmit={handleTrySubmit}>
              <section className="p-2">
                <IconSelector value={icon} onChange={handleIconChange} disable={canEdite} descriptionKey="interactionDescriptions:pages.products.category.categoryPupop.input.namePlaceHolder" />

                <div className={defaultStyle.divisionBar}></div>

                <InputField
                  type="text"
                  value={name}
                  icon={faFileSignature}
                  maxLength={INPUT_SIZE.EXTRA_SERVICE.name}
                  placeholder={t("label_name_place_holder")}
                  onInvalidMessage={t("label_name_onInvalidMessage")}
                  onChange={handleNameChange}
                  required
                  disable={canEdite}

                  descriptionKey="interactionDescriptions:pages.products.category.categoryPupop.input.descriptionPlaceHolder" />

                <GetSpace top={10} />

                <InputField
                  maxLength={INPUT_SIZE.EXTRA_SERVICE.description}
                  type="text"
                  icon={faFile}
                  value={description}
                  placeholder={t("label_description_place_holder")}
                  onInvalidMessage={t("label_description_onInvalidMessage")}
                  onChange={handleDescriptionChange}
                  disable={canEdite}

                  descriptionKey="interactionDescriptions:pages.products.category.categoryPupop.input.tLabelDescriptionPlaceHolder" />

                <GetSpace top={5} />
              </section>

              <div className={defaultStyle.divisionBar}></div>

              <section className={defaultStyle.buttonsController}>
                <CanView permission={Permissions.CanEditeCategories}>
                  <Button
                    label={t("buttons:button_save")}
                    typeButton="submit"
                    type="primary"
                    size="medium"
                    bgColor={THEME.colors.buttonBGSaveColor}

                  descriptionKey="interactionDescriptions:pages.products.category.categoryPupop.button.save" />
                </CanView>

                {category && category.id !== -1 && (
                  <>
                    <GetSpace right={5} />

                    <Button
                      label={t("buttons:button_register")}
                      typeButton="button"
                      type="primary"
                      size="medium"
                      bgColor={THEME.colors.buttonBGRegisterColor}
                      onClick={handleEnableRegister}

                  descriptionKey="interactionDescriptions:pages.products.category.categoryPupop.button.register" />

                    <GetSpace right={5} />
                    <Button
                      label={t("buttons:button_view_associations")}
                      typeButton="button"
                      type="primary"
                      size="medium"
                      bgColor={THEME.colors.buttonBGPlusColor}
                      onClick={() => setShowAssociations(true)}

                  descriptionKey="interactionDescriptions:pages.products.category.categoryPupop.button.viewAssociations" />

                    <CanView permission={Permissions.CanEditeCategories}>
                      {onCopy && (
                        <>
                          <GetSpace right={5} />

                          <Button
                            label={t("buttons:button_copy")}
                            typeButton="button"
                            type="primary"
                            size="medium"
                            bgColor={THEME.colors.buttonBGPlusColor}
                            onClick={onCopy}

                  descriptionKey="interactionDescriptions:pages.products.category.categoryPupop.button.copy" />
                        </>
                      )}
                    </CanView>
                  </>
                )}

                <CanView permission={Permissions.CanEditeCategories}>
                  {category && category.id !== -1 && (
                    <>
                      <GetSpace right={5} />

                      <Button
                        label={t("buttons:button_remove")}
                        typeButton="button"
                        type="primary"
                        size="medium"
                        iconColor={THEME.colors.buttonFGRemoveColor}
                        bgColor={THEME.colors.buttonBGRemoveColor}
                        onClick={() =>
                          setNewConfirmation(
                            t("general:label_title_remove"),
                            t("general:label_description_remove"),
                            async () => {
                              await handleRemove(category!, () => handleGetCategory(onClonse))
                            },
                          )
                        }

                  descriptionKey="interactionDescriptions:pages.products.category.categoryPupop.button.remove" />
                    </>
                  )}
                </CanView>
              </section>
            </form>

            {isRegisterEnable && category && (
              <ViewRegister id={category.id} typeEntity="CategoryEntityId" onClose={handleDisableRegister} />
            )}

            {showAssociations && category && (
              <AssociationsPopup
                title={t("general:label_related_title", { name: category.name })}
                emptyMessage={t("general:label_related_empty")}
                onClose={() => setShowAssociations(false)}
                items={resolveCategoryAssociations(category, productsMemory.listData, catalogItemsMemory.listData, comboMemory.listData)}
              />
            )}
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
